import React, { useState, useEffect } from 'react';
import Table from '../Table';
import { IQuestionnaire } from '../../types/questionnaire';
import { FormBuilderService } from '../../api/FormBuilderService';
import Button from '../Button';

interface QuestionnairesSectionProps {
  questionnaires: IQuestionnaire[];
  onAddToJob: (jobNumber: number, itemId: string) => void;
  onPreview: (itemId: string) => void;
  onStatusChange: (questionnaireId: string, newStatus: boolean) => Promise<void>;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  itemsPerPage: number;
  onItemsPerPageChange: (size: number) => void;
  pageSizeOptions: number[];
}

const QuestionnairesSection: React.FC<QuestionnairesSectionProps> = ({
  questionnaires,
  onAddToJob,
  onPreview,
  onStatusChange,
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage,
  onItemsPerPageChange,
  pageSizeOptions
}) => {
  const [activeJobsCounts, setActiveJobsCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    const fetchActiveJobsCounts = async () => {
      if (!questionnaires?.length) return;

      try {
        // Fetch all counts in parallel
        const countsPromises = questionnaires.map(q =>
          FormBuilderService.getActiveJobsCount(q.questionnaireId)
            .then(count => ({ id: q.questionnaireId, count }))
            .catch(() => ({ id: q.questionnaireId, count: 0 }))
        );

        const results = await Promise.all(countsPromises);

        // Convert results to a map
        const countsMap: Record<string, number> = {};
        results.forEach(({ id, count }) => {
          countsMap[id] = count;
        });

        setActiveJobsCounts(countsMap);
      } catch (error) {
        console.error('Error fetching active jobs counts:', error);
      }
    };

    fetchActiveJobsCounts();
  }, [questionnaires]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <div className='mt-2 mb-2 text-center'>
      <div className='font-weight-bold py-2 h3'>Questionnaires</div>
      {questionnaires.length === 0 ? (
        <div className="mt-2 mb-2 text-center">
          <img
            src="/svgs/no-results.svg"
            alt="No questionnaires found"
          />
        </div>
      ) : (
        <>
          <div className="d-flex align-items-center mt-3 form-group">
            <label htmlFor="itemsPerPage" className="me-2">Items per page:</label>
            <select
              id="itemsPerPage"
              className="form-control select2 mx-2"
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              style={{ width: 'auto' }}
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          <Table
            data={questionnaires}
            activeJobsCounts={activeJobsCounts}
            onAddToJob={onAddToJob}
            onPreview={onPreview}
            onStatusChange={onStatusChange}
          />
          <div className="d-flex justify-content-center align-items-center mt-3 gap-3">
            <div className="d-flex align-items-center gap-2">
              <Button
                text="Previous"
                variant="outline-primary"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              />
              <span className="mx-2">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                text="Next"
                variant="outline-primary"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default QuestionnairesSection; 